import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_END_POINTS } from './apiEndpoints';
import { baseUrl } from './baseUrl';

const rawBaseQuery = fetchBaseQuery({
    baseUrl: baseUrl.MAIN_BASE_URL,
});

type ExtraOptions = {
    headers?: Record<string, string>;
};

const CACHE_NAME = 'unauth-api-cache'; // Separate cache name for unauthenticated API
const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds

// Helper function to check if cached data is expired
const isCacheExpired = (timestamp: number) => {
    return Date.now() - timestamp > CACHE_DURATION;
};

// Helper function to clean up expired cache entries
const cleanUpExpiredCache = async () => {
    const cache = await caches.open(CACHE_NAME);
    const keys = await cache.keys();

    for (const request of keys) {
        const cachedResponse = await cache.match(request);
        if (cachedResponse) {
            const cachedData = await cachedResponse.json();
            if (isCacheExpired(cachedData.timestamp)) {
                await cache.delete(request);
            }
        }
    }
};

// Schedule cache cleanup
const scheduleCacheCleanup = () => {
    setInterval(cleanUpExpiredCache, CACHE_DURATION);
};

// Initialize cache cleanup scheduling when the module loads
scheduleCacheCleanup();

// Function to check if the request has query parameters
const hasQueryParams = (url: string, params?: Record<string, any>) => {
    return url.includes('?') || (params && Object.keys(params).length > 0);
};

// Function to fetch data from Cache Storage
const fetchFromCache = async (url: string, params?: Record<string, any>) => {
    if (hasQueryParams(url, params)) {
        return null;
    }

    const cache = await caches.open(CACHE_NAME);
    const cachedResponse = await cache.match(url);
    if (cachedResponse) {
        const cachedData = await cachedResponse.json();
        if (!isCacheExpired(cachedData.timestamp)) {
            return cachedData.data;
        }
        await cache.delete(url);
    }
    return null;
};

// Function to save data to Cache Storage
const saveToCache = async (url: string, data: any, params?: Record<string, any>) => {
    if (hasQueryParams(url, params)) {
        return;
    }

    const cache = await caches.open(CACHE_NAME);
    const response = new Response(
        JSON.stringify({ data, timestamp: Date.now() }),
        { headers: { 'Content-Type': 'application/json' } }
    );
    await cache.put(url, response);
};

const publicBaseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    ExtraOptions
> = async (args, api, extraOptions) => {
    const urlEnd = typeof args === 'string' ? args : args.url;
    const adjustedUrl = `${urlEnd}`;
    const adjustedArgs = typeof args === 'string' ? { url: adjustedUrl } : { ...args, url: adjustedUrl };
    const params = adjustedArgs.params;

    // No authentication headers needed for public endpoints
    const mergedHeaders = { ...extraOptions?.headers };

    // Check Cache Storage before making API request
    const cachedData = await fetchFromCache(adjustedUrl, params);
    if (cachedData) {
        return { data: cachedData };
    }

    // Fetch from API if not in cache or cache expired
    const result = await rawBaseQuery({ ...adjustedArgs, headers: mergedHeaders }, api, extraOptions);

    // Save to Cache Storage after fetching
    if (result.data) {
        await saveToCache(adjustedUrl, result.data, params);
    }

    return result;
};

export const UnauthApi = createApi({
    reducerPath: 'UnauthApi',
    baseQuery: publicBaseQuery,
    endpoints: (builder) => ({
        getAllTimeZone: builder.query({
            query: () => {
                return {
                    url: '/API/General' + API_END_POINTS.getAllTimeZoneV2,
                    method: "GET"
                }
            },
        }),
        getAllLocations: builder.query({
            query: () => {
                return {
                    url: '/API/General' + API_END_POINTS.getAllLocationsV2,
                    method: "GET"
                }
            },
        }),
        departmentsLookup: builder.query({
            query: () => {
                return {
                    url: '/API/General' + API_END_POINTS.getAllDepartmentV2,
                    method: "GET"
                }
            },
        }),
        designationLookup: builder.query({
            query: (params) => {
                return {
                    url: '/API/General' + `${API_END_POINTS.getAllDesignationV2}`,
                    method: "GET",
                    params
                }
            },
        }),
        getAllDivision: builder.query({
            query: () => {
                return {
                    url: '/API/General' + `${API_END_POINTS.getAllDivisionLookupV2}`,
                    method: "GET",
                }
            },
        }),
        getIsTravelModuleAccessAvailable: builder.query({
            query: () => ({
                url: '/API/Account' + API_END_POINTS.getIsTravelModuleAccessAvailable,
                method: "GET",
            }),
        }),
    }),
});

export const {
    useGetAllTimeZoneQuery,
    useGetAllLocationsQuery,
    useDepartmentsLookupQuery,
    useDesignationLookupQuery,
    useGetAllDivisionQuery,
    useGetIsTravelModuleAccessAvailableQuery,
} = UnauthApi;